import { formatCalendarDate, formatClockTime } from './dateFormatUtils';
import { getStoreWaitlistPath } from '../pages/stores/storeHubTabs';

export const parseStaffNotificationPayload = (payloadJson) => {
  if (!payloadJson) {
    return {};
  }
  if (typeof payloadJson === 'object') {
    return payloadJson;
  }
  try {
    return JSON.parse(payloadJson);
  } catch {
    return {};
  }
};

export const isSupportChatNotification = (notification) => {
  const type = String(notification?.type ?? '');
  return type === 'SupportChat' || type === '3';
};

export const isAppointmentAgendaNotification = (notification) => {
  const type = String(notification?.type ?? '');
  return type === 'NewAppointment'
    || type === '0'
    || type === 'CancelledAppointment'
    || type === '1'
    || type === 'AppointmentUpdated'
    || type === '7'
    || type === 'AppointmentConfirmationRequested'
    || type === '6';
};

export const isWaitlistNotification = (notification) => {
  const type = String(notification?.type ?? '');
  return type === 'WaitlistJoined'
    || type === '4'
    || type === 'WaitlistSlotOpened'
    || type === '5';
};

export const isAppointmentConfirmationRequestedNotification = (notification) => {
  const type = String(notification?.type ?? '');
  return type === 'AppointmentConfirmationRequested' || type === '6';
};

export const isStaffWorkspacePath = (pathname) => {
  if (!pathname?.startsWith('/management')) {
    return false;
  }
  return !pathname.startsWith('/management/team-member/login')
    && !pathname.startsWith('/management/app-admin/login');
};

export const getSafeManagementReturnPath = (value) => {
  if (!value || typeof value !== 'string') {
    return null;
  }

  const path = value.trim();
  if (!path.startsWith('/management/') || path.startsWith('//') || path.includes('://') || path.includes('\\')) {
    return null;
  }

  return path;
};

export const buildManagementAppointmentsPath = ({
  appointmentId,
  date,
  from,
  bookCustomerId,
  startTime,
} = {}) => {
  const params = new URLSearchParams();
  if (appointmentId) {
    params.set('appointment', String(appointmentId));
  }

  const dateKey = date ? String(date).slice(0, 10) : '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) {
    params.set('date', dateKey);
  }

  if (bookCustomerId) {
    params.set('bookCustomer', String(bookCustomerId));
  }

  const time = startTime ? String(startTime).slice(0, 5) : '';
  if (/^\d{2}:\d{2}$/.test(time)) {
    params.set('startTime', time);
  }

  const returnPath = getSafeManagementReturnPath(from);
  if (returnPath) {
    params.set('from', returnPath);
  }

  const query = params.toString();
  return query ? `/management/appointments?${query}` : '/management/appointments';
};

const getAppointmentId = (notification, payload) => (
  notification?.appointmentId || payload.appointmentId || null
);

const getWaitlistStoreId = (notification, payload) => (
  notification?.storeId || payload.storeId || null
);

export const getWaitlistAlertFromNotification = (notification) => {
  if (!isWaitlistNotification(notification)) {
    return null;
  }

  const payload = parseStaffNotificationPayload(notification.payloadJson);
  const storeId = getWaitlistStoreId(notification, payload);
  if (!storeId) {
    return null;
  }

  const type = String(notification?.type ?? '');
  const isSlotOpened = type === 'WaitlistSlotOpened' || type === '5';
  return {
    ...payload,
    storeId,
    alertType: payload.alertType || (isSlotOpened ? 'slotOpened' : 'joined'),
  };
};

export const getAppointmentConfirmationAlertFromNotification = (notification) => {
  if (!isAppointmentConfirmationRequestedNotification(notification)) {
    return null;
  }

  const payload = parseStaffNotificationPayload(notification.payloadJson);
  const appointmentId = getAppointmentId(notification, payload);
  if (!appointmentId) {
    return null;
  }

  return {
    ...payload,
    storeId: notification?.storeId || payload.storeId || null,
    appointmentId,
    customerName: payload.customerName || '',
    date: payload.date || '',
    begin: payload.begin || '',
    end: payload.end || '',
    openToAnyProfessional: Boolean(payload.openToAnyProfessional),
  };
};

export const getStaffNotificationPath = (notification) => {
  if (isSupportChatNotification(notification)) {
    const conversationId = parseStaffNotificationPayload(notification.payloadJson).conversationId;
    return conversationId
      ? `/management/support?conversation=${encodeURIComponent(conversationId)}`
      : '/management/support';
  }

  if (isWaitlistNotification(notification)) {
    const payload = parseStaffNotificationPayload(notification.payloadJson);
    const storeId = getWaitlistStoreId(notification, payload);
    return getStoreWaitlistPath(storeId);
  }

  if (!isAppointmentAgendaNotification(notification)) {
    return null;
  }

  const payload = parseStaffNotificationPayload(notification.payloadJson);
  const appointmentId = getAppointmentId(notification, payload);
  if (!appointmentId) {
    return '/management/appointments';
  }

  return buildManagementAppointmentsPath({
    appointmentId,
    date: payload.date,
  });
};

export const getStaffNotificationTextValues = (notification) => {
  const payload = parseStaffNotificationPayload(notification?.payloadJson);
  return {
    customerName: payload.customerName || '',
    storeName: payload.storeName || '',
    message: payload.message || '',
    date: formatCalendarDate(payload.date),
    begin: formatClockTime(payload.begin),
    end: formatClockTime(payload.end),
  };
};

export const getStaffNotificationTitle = (notification, t) => (
  t(notification?.titleKey || 'staffNotifications.other.title', getStaffNotificationTextValues(notification))
);

export const getStaffNotificationBody = (notification, t) => (
  t(notification?.bodyKey || 'staffNotifications.other.body', getStaffNotificationTextValues(notification))
);
